/**
 * Tutorial - Interactive onboarding for new users
 */

import { useState, useEffect } from 'react';
import './ui/ui.css';

const TUTORIAL_COMPLETED_KEY = 'genesis_tutorial_completed';

interface TutorialStep {
  title: string;
  content: string;
  icon: string;
}

const tutorialSteps: TutorialStep[] = [
  {
    title: 'Bienvenido a GENESIS',
    content: 'GENESIS es un simulador de vida artificial donde puedes crear y guiar criaturas con redes neuronales.',
    icon: '🧬',
  },
  {
    title: 'Crea tu Criatura',
    content: 'Usa el boton "Crear Criatura" en el panel izquierdo para dar vida a tu criatura. Aparecera en el mundo y comenzara a explorar.',
    icon: '✨',
  },
  {
    title: 'Guia su Comportamiento',
    content: 'Ajusta los sliders de "Guia de Comportamiento" para influir en las decisiones de tu criatura. El cerebro neural decide, pero tus preferencias importan.',
    icon: '🧠',
  },
  {
    title: 'Mantente Vivo',
    content: 'Tu criatura necesita energia para sobrevivir. Busca comida (puntos verdes) y evita quedarte sin energia. Observa la barra de energia en el panel.',
    icon: '⚡',
  },
  {
    title: 'Explora el Mundo',
    content: 'Usa el minimapa para navegar. Haz click en el mundo para establecer waypoints y guiar a tu criatura hacia objetivos especificos.',
    icon: '🗺️',
  },
];

interface TutorialProps {
  forceShow?: boolean;
  onComplete?: () => void;
}

export function Tutorial({ forceShow = false, onComplete }: TutorialProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    // Check if tutorial was already completed
    const completed = localStorage.getItem(TUTORIAL_COMPLETED_KEY);
    if (!completed || forceShow) {
      setIsVisible(true);
    }
  }, [forceShow]);

  const handleNext = () => {
    if (currentStep < tutorialSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    handleComplete();
  };

  const handleComplete = () => {
    localStorage.setItem(TUTORIAL_COMPLETED_KEY, 'true');
    setIsVisible(false);
    onComplete?.();
  };

  if (!isVisible) return null;

  const step = tutorialSteps[currentStep];
  const isLastStep = currentStep === tutorialSteps.length - 1;
  const isFirstStep = currentStep === 0;

  return (
    <div className="tutorial-overlay" role="dialog" aria-labelledby="tutorial-title">
      <div className="tutorial-dialog">
        <div className="tutorial-header">
          <div className="tutorial-progress">
            {tutorialSteps.map((_, index) => (
              <div
                key={index}
                className={`progress-dot ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
                aria-label={`Paso ${index + 1} de ${tutorialSteps.length}`}
              />
            ))}
          </div>
          <button
            className="tutorial-skip"
            onClick={handleSkip}
            aria-label="Omitir tutorial"
          >
            Omitir
          </button>
        </div>

        <div className="tutorial-content">
          <div className="tutorial-icon" aria-hidden="true">
            {step.icon}
          </div>
          <h2 id="tutorial-title" className="tutorial-title">
            {step.title}
          </h2>
          <p className="tutorial-text">{step.content}</p>
        </div>

        <div className="tutorial-actions">
          {!isFirstStep && (
            <button className="tutorial-btn secondary" onClick={handlePrev}>
              Anterior
            </button>
          )}
          <button className="tutorial-btn primary" onClick={handleNext}>
            {isLastStep ? 'Comenzar' : 'Siguiente'}
          </button>
        </div>

        <div className="tutorial-step-indicator">
          {currentStep + 1} / {tutorialSteps.length}
        </div>
      </div>
    </div>
  );
}

// Hook to reset tutorial (for testing)
export function useResetTutorial() {
  return () => {
    localStorage.removeItem(TUTORIAL_COMPLETED_KEY);
  };
}
